/**
 * Live Layer slice — source of truth for the right-inspector LL panel and
 * the full LL workspace. Populated from two places:
 *   • ast/pipeline/runFilesAstSync — parses `live-layer.schema.json` from the
 *     editor's loaded files and dispatches LIVE_LAYER_SCHEMA_SET.
 *   • EditorViewV2 mount — fetches taxonomy from the server once on appId
 *     change and dispatches LIVE_LAYER_TAXONOMY_SET.
 *
 * Panels never hit /api/apps/:id/live-layer/schema directly — the schema
 * lives in the user's files already, so a round-trip would be redundant AND
 * flash the empty-state card on every mode switch before the network resolves.
 */
import isEqual from 'lodash/isEqual';

import type { LLGroup, LLTaxonomy } from '@/features/editor/live-layer/types/live-layer.types';
import type { RootState } from '@/store/rootState';

/**
 * Static "which JSX element consumes an LL value" map. Computed once per AST
 * sync over `src/screens/**` by scanning `useLiveLayer(...)` calls and
 * threading the resulting variables into their JSX consumers. Keyed by the
 * babel-stamped triple `<file>:<line>:<col>` — the same values the run-engine
 * emits on `data-0/1/2` for every host element, so runtime lookup is O(1).
 */
export interface LiveLayerBinding {
  /** Schema field id in dot notation (e.g. `home.hero.headline`). */
  path: string;
  /** Enclosing group id — first segment of `path`. Focusing "home.hero"
   *  dims sibling groups in the panel. */
  groupId: string;
}

export interface LiveLayerState {
  appId: string | null;
  schema: LLGroup[] | null;
  schemaSource: string | null;
  schemaError: string | null;
  schemaSynced: boolean;
  taxonomy: LLTaxonomy;
  taxonomyLoaded: boolean;
  /** `<file>:<line>:<col>` → binding. Populated by `syncLiveLayerBindings`. */
  bindings: Record<string, LiveLayerBinding>;
  /**
   * `path` → the `?? default` literal at the useLiveLayer call site.
   *
   * `useLiveLayer` returns undefined with nothing published, so this literal is
   * what the app actually renders in the un-overridden case. The panel resolves
   * overrides only, so without it every un-set field read blank — and a boolean
   * rendered `off` while the app rendered it `true`.
   */
  sourceDefaults: Record<string, unknown>;
}

const liveLayerInitial: LiveLayerState = {
  appId: null,
  schema: null,
  schemaSource: null,
  schemaError: null,
  schemaSynced: false,
  taxonomy: {},
  taxonomyLoaded: false,
  bindings: {},
  sourceDefaults: {},
};

export type LiveLayerAction =
  | {
      type: 'LIVE_LAYER_SCHEMA_SET';
      schema: LLGroup[] | null;
      source: string | null;
      error: string | null;
    }
  | { type: 'LIVE_LAYER_TAXONOMY_SET'; taxonomy: LLTaxonomy }
  | {
      type: 'LIVE_LAYER_BINDINGS_SET';
      bindings: Record<string, LiveLayerBinding>;
      sourceDefaults: Record<string, unknown>;
    }
  | { type: 'LIVE_LAYER_RESET_FOR_APP'; appId: string };

export function liveLayer(state: LiveLayerState = liveLayerInitial, action: any): LiveLayerState {
  switch ((action as LiveLayerAction).type) {
    case 'LIVE_LAYER_RESET_FOR_APP': {
      const a = action as Extract<LiveLayerAction, { type: 'LIVE_LAYER_RESET_FOR_APP' }>;
      if (state.appId === a.appId) return state;
      return { ...liveLayerInitial, appId: a.appId };
    }
    case 'LIVE_LAYER_SCHEMA_SET': {
      const a = action as Extract<LiveLayerAction, { type: 'LIVE_LAYER_SCHEMA_SET' }>;
      if (
        state.schemaSynced &&
        state.schemaSource === a.source &&
        state.schemaError === a.error &&
        isEqual(state.schema, a.schema)
      )
        return state;
      return {
        ...state,
        schema: a.schema,
        schemaSource: a.source,
        schemaError: a.error,
        schemaSynced: true,
      };
    }
    case 'LIVE_LAYER_TAXONOMY_SET': {
      const a = action as Extract<LiveLayerAction, { type: 'LIVE_LAYER_TAXONOMY_SET' }>;
      if (state.taxonomyLoaded && isEqual(state.taxonomy, a.taxonomy)) return state;
      return { ...state, taxonomy: a.taxonomy, taxonomyLoaded: true };
    }
    case 'LIVE_LAYER_BINDINGS_SET': {
      const a = action as Extract<LiveLayerAction, { type: 'LIVE_LAYER_BINDINGS_SET' }>;
      if (isEqual(state.bindings, a.bindings) && isEqual(state.sourceDefaults, a.sourceDefaults)) {
        return state;
      }
      return { ...state, bindings: a.bindings, sourceDefaults: a.sourceDefaults };
    }
    default:
      return state;
  }
}

export const setLiveLayerSchema = (
  schema: LLGroup[] | null,
  source: string | null,
  error: string | null = null,
): LiveLayerAction => ({
  type: 'LIVE_LAYER_SCHEMA_SET',
  schema,
  source,
  error,
});

export const setLiveLayerTaxonomy = (taxonomy: LLTaxonomy): LiveLayerAction => ({
  type: 'LIVE_LAYER_TAXONOMY_SET',
  taxonomy,
});

export const setLiveLayerBindings = (
  bindings: Record<string, LiveLayerBinding>,
  sourceDefaults: Record<string, unknown> = {},
): LiveLayerAction => ({
  type: 'LIVE_LAYER_BINDINGS_SET',
  bindings,
  sourceDefaults,
});

export const selectLiveLayerSchema = (state: RootState) => state.liveLayer.schema;
export const selectLiveLayerSchemaError = (state: RootState) => state.liveLayer.schemaError;
export const selectLiveLayerSchemaSynced = (state: RootState) => state.liveLayer.schemaSynced;
export const selectLiveLayerTaxonomy = (state: RootState) => state.liveLayer.taxonomy;
export const selectLiveLayerBindings = (state: RootState) => state.liveLayer.bindings;
export const selectLiveLayerSourceDefaults = (state: RootState) => state.liveLayer.sourceDefaults;
